import "./index.scss";
import { AppDetailController } from "./controller";
import { Spin, Tabs } from "antd";
import { ApplicationReviewActionBar } from "../../../parts/AppInfo/ReviewActionBar";
import React, { useEffect } from "react";
import { ApplicationUserAccess } from "./tabs/static/userAccess";
import { ApplicationsReview } from "./tabs/static/reviewHistory";
import { ReviewTasks } from "./tabs/dynamic/review";
import { appInfoTab, reviewTab, usersTab } from "./model";
import { AppInfoComponent } from "./tabs/static/appInfo";
import { ColumnSelectPaginationPortal } from "../../../components/authZTable/column-select-pagination-portal";
import { MultiSelect } from "../../../components/multiSelect";
import { sendSwitchTabEvent, sendTableEvent, TableAction } from "../../../components/matomoClient";
import { Routes } from "../../../common/Routes";
import { checkIfUserIsGlobalAccessApprover } from "../../../common/CheckIfUserIsGlobalAccessApprover";
import AccessRequest from "../../../components/Microfrontend";
import { TestOneDevId } from "../../../common/dev";
import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from "../../../common/utils";
import AuthZButton from "src/components/AuthzButton/index";
import UploadReviewCycleFiles from "../upload-review-cycle-files";
import { getAppAccessTypeLink } from "../../../common/AccessTypeLinks";

export const ApplicationDetail = ({ match }) => {
  const { applicationUniqueName: appUniqueName, tabName } = match.params;

  const appDetailController = AppDetailController(appUniqueName);
  const {
    appId,
    appDetail: appInfo,
    isLoading,
    refreshAppDetail,
    sendGetUserAccessDataEmail,
    startReview,
    closeReview,
    reviews,
    onTabPaneRemove,
    setActiveTabId,
    activeTabId,
    refreshReviewHistory,
    openActiveReviewTab,
    closeActiveReviewTab,
    tabPageSize,
    setTabPageSize,
    paginationPortal,
    totalColumns,
    selectedColumns,
    pinnedColumns,
    setSelectedColumns,
    onReviewClick,
    accessRequestMicrofrontendHostUrl,
    isAccessRequestEnabled,
    getReviewTicket,
    isReviewCycleEnabled,
    latestReviewId,
  } = appDetailController;
  const accessTypeLink = getAppAccessTypeLink(appInfo)?.url;

  const onTabPaneEdit = (targetKey, action) => {
    if (action === "remove") {
      onTabPaneRemove(targetKey);
    } else {
      // Pane add logic goes here
    }
  };

  function accessRequest() {
    if (appInfo.name === "Test One Dev") {
      if (isAccessRequestEnabled) {
        return (
          <AccessRequest
            microFrontendUrl={accessRequestMicrofrontendHostUrl}
            name={"AccessRequest"}
            application={{ name: appInfo.name, id: TestOneDevId }}
          />
        );
      }
    }
    return null;
  }

  function removeParamsInLink() {
    window.history.replaceState({}, "", Routes.Applications.path + "/" + appUniqueName);
  }

  function openTab(tabDetails) {
    onReviewClick(tabDetails);
    setActiveTabId(tabDetails.reviewId);
  }

  function checkIfReviewTabsShouldBeVisible() {
    return !(appInfo?.isReviewable === false
      && isNullOrUndefinedOrEmpty(appInfo?.recentReviews?.lastClosedReview)
      && isNullOrUndefinedOrEmpty(appInfo?.recentReviews?.inProgressReview));
  }

  async function redirectToRequestedTab() {
    if (tabName === "appInfo") {
      setActiveTabId(appInfoTab.id);
      removeParamsInLink();
    } else if (tabName === "activeReview") {
      const params = new URLSearchParams(window.location.search);
      const currentActiveReviewTabData = {
        reviewId: params.get("reviewId"),
        name: params.get("reviewName"),
        status: "In Progress",
        appId: appId,
      };
      openTab(currentActiveReviewTabData);
      removeParamsInLink();
    } else if (tabName === "lastReviewed") {
      const params = new URLSearchParams(window.location.search);
      const lastReviewTabData = {
        reviewId: params.get("reviewId"),
        name: params.get("reviewName"),
        status: "Closed",
        appId: appId,
      };
      openTab(lastReviewTabData);
      removeParamsInLink();
    } else {
      removeParamsInLink();
    }
  }

  function setDefaultTab() {
    if (isNullOrUndefined(activeTabId)) {
      appInfo?.isReviewable === false
        ? setActiveTabId(`${usersTab.id}-${appId}`)
        : setActiveTabId(reviewTab.id);
    } else setActiveTabId(activeTabId);
  }

  useEffect(() => {
    if (!isNullOrUndefinedOrEmpty(appInfo)) {
      if (tabName !== undefined) {
        redirectToRequestedTab();
      } else setDefaultTab();
    }
  }, [appInfo?.id]);

  function renderTabs() {
    const isReviewTabActive = activeTabId === reviewTab.id;
    return (
      <Tabs
        type="editable-card"
        activeKey={activeTabId}
        onTabClick={(key) => {
          setActiveTabId(key);
          sendSwitchTabEvent(key);
        }}
        onEdit={onTabPaneEdit}
        hideAdd
      >
        <Tabs.TabPane
          tab={`User's Access (${appInfo?.lastExportedOn || "Not uploaded yet"})`}
          key={usersTab.id + "-" + appId}
          closable={false}
        >
          <ApplicationUserAccess
            appId={appId}
            pageSize={tabPageSize}
            setTabPageSize={setTabPageSize}
            activeTabId={activeTabId}
            accessTypeLink={accessTypeLink}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={appInfoTab.name}
          key={appInfoTab.id}
          closable={false}
        >
          <AppInfoComponent
            appInfo={appInfo}
            pageSize={tabPageSize}
            setTabPageSize={setTabPageSize}
          />
        </Tabs.TabPane>
        {checkIfReviewTabsShouldBeVisible() ? (
          <>
            <Tabs.TabPane
              tab={reviewTab.name}
              key={reviewTab.id}
              closable={false}
            >
              <ApplicationsReview
                appInfo={appInfo}
                appId={appId}
                reload={isReviewTabActive}
                pageSize={tabPageSize}
                setTabPageSize={setTabPageSize}
                setActiveTab={setActiveTabId}
              />
            </Tabs.TabPane>
            {reviews.map(
              (review) => {
                return review.appId === appId && (
                  <Tabs.TabPane
                    tab={`${review.name} (${review.status})`}
                    key={review.reviewId}
                    closable={true}
                  >
                    <ReviewTasks
                      appId={appId}
                      reviewId={review.reviewId}
                      status={review.status}
                      pageSize={tabPageSize}
                      setTabPageSize={setTabPageSize}
                      reviewName={review.name}
                      activeTabId={activeTabId}
                      accessTypeLink={accessTypeLink}
                      appHeader={renderApplicationHeader()}
                    />
                  </Tabs.TabPane>
                );
              },
            )}
          </>
        ) : null}
      </Tabs>
    );
  }

  function renderApplicationHeader() {
    return (
      <Spin spinning={isLoading} delay={500} style={{ height: 70 }}>
        <div className={"appDetailHeader"}>
          <div className={"appName"}>
            <div>
              <span>
                {appInfo?.name + " "}
                {appInfo?.name ? accessRequest() : null}
              </span>
            </div>
            <div>
              {isReviewCycleEnabled ? (
                <UploadReviewCycleFiles
                  modalTriggerElement={
                    <AuthZButton
                      authZButtonType="primary"
                    >
                      Upload Files for Review Cycle (Mar,2023)
                    </AuthZButton>
                  }
                  appInfo={{
                    name: appInfo?.name,
                    id: appId,
                    hasUploadPermission: appInfo?.hasUploadPermission,
                  }}
                />
              ) : null}
            </div>
          </div>
          <ApplicationReviewActionBar
            appInfo={appInfo}
            appId={appId}
            refreshAppDetail={refreshAppDetail}
            showGetUserDataBtn={checkIfUserIsGlobalAccessApprover(appInfo)}
            showReviewBtn={checkIfUserIsGlobalAccessApprover(appInfo)}
            sendGetUserAccessDataEmail={sendGetUserAccessDataEmail}
            startReview={startReview}
            closeReview={closeReview}
            getReviewTicket={getReviewTicket}
            refreshReviewHistory={refreshReviewHistory}
            openActiveReviewTab={openActiveReviewTab}
            closeActiveReview={closeActiveReviewTab}
            setActiveTab={setActiveTabId}
            showStatusAsNotReviewable={!checkIfReviewTabsShouldBeVisible()}
            latestReviewId={latestReviewId}
          />
        </div>
      </Spin>
    );
  }

  return (
    <div className={"appDetail"}>
      {renderApplicationHeader()}
      <div className={"user-access-container"}>
        <Spin spinning={isLoading} style={{ justifyContent: "center" }}>
          {renderTabs()}
        </Spin>
      </div>
      {paginationPortal && (
        <ColumnSelectPaginationPortal portalRoot={paginationPortal} id={activeTabId}>
          <MultiSelect
            placeHolder={"Select Columns"}
            options={totalColumns}
            selected={selectedColumns}
            onSelected={(value) => {
              setSelectedColumns(value);
              sendTableEvent(TableAction.SelectColumn, value);
            }}
            pinned={pinnedColumns}
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
          />
        </ColumnSelectPaginationPortal>
      )}
    </div>
  );
};
